import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { useMe } from "@/app/session";
import { RequestError, useDraft, usePublish, useSaveDraft, useTree, useUsage, type Tree } from "@/app/queries";
import { Button } from "@/ui/Button";
import { EmptyState, NoAccess, SkeletonRows, StateCard, Toast, type ToastTone } from "@/ui/states";
import { plural } from "@/lib/plural";
import { StructureTree } from "./editor/StructureTree";
import { FeedbackBadge } from "./editor/FeedbackBadge";
import { ResourcesTab } from "./editor/ResourcesTab";
import { PracticeTab } from "./editor/PracticeTab";
import { BlocksTab } from "./editor/BlocksTab";
import { FilesTab } from "./editor/FilesTab";
import { GateTab } from "./editor/GateTab";
import { ReadinessCard } from "./editor/ReadinessCard";
import { DepsCard } from "./editor/DepsCard";
import { UsageCard } from "./editor/UsageCard";
import { HistoryCard } from "./editor/HistoryCard";
import { KbImport } from "./editor/KbImport";
import { useAutosave, type SaveStatus } from "./editor/useAutosave";
import { blocksOn, BLOCKS_TOTAL, locate, type Patch } from "./editor/shared";

export const NO_ACCESS_BODY = "Конструктор курса доступен администраторам и владельцу школы. Если вам нужно править шаги — попросите роль у администратора.";

const NETWORK = {
  title: "Нет связи с сервером",
  body: "Страница не смогла получить данные. Ничего не потеряно — обновите страницу, когда связь вернётся.",
};

/* Шапка приложения 55px + топбар конструктора 55px: колонки скроллятся сами. */
const BODY_H = "calc(100vh - 110px)";

/** A1 «Конструктор курса»: дерево · редактор шага · готовность и статистика. */
export function CoursePage({ stepId }: { stepId?: string }) {
  const me = useMe();
  if (me.status === "pending") {
    return (
      <Shell screen="конструктор курса">
        <SkeletonRows />
      </Shell>
    );
  }
  if (me.status === "error") {
    return (
      <Shell screen="конструктор курса">
        <StateCard code="ERR_NETWORK" where="загрузка" tone="warn" title={NETWORK.title} body={NETWORK.body} primary={{ label: "повторить сейчас", onClick: () => void me.refetch() }} />
      </Shell>
    );
  }
  const u = me.data;
  if (!u) return null;
  const user = { name: u.name, role: ROLE_LABEL[u.role] ?? u.role };
  if (u.role !== "admin" && u.role !== "owner") {
    return (
      <Shell screen="конструктор курса" user={user}>
        <NoAccess where="конструктор курса" body={NO_ACCESS_BODY} primary={{ label: "на мой маршрут", href: "/" }} />
      </Shell>
    );
  }
  return (
    <Shell wide screen="конструктор курса" user={user}>
      <Builder stepId={stepId} />
    </Shell>
  );
}

function Builder({ stepId }: { stepId?: string }) {
  const tree = useTree();
  if (tree.status === "pending") {
    return (
      <div className="pt-6">
        <SkeletonRows />
      </div>
    );
  }
  if (tree.status === "error") {
    const err = tree.error;
    return (
      <div className="mx-auto max-w-[720px] px-8 pt-8">
        {err instanceof RequestError && err.status === 403 ? (
          <NoAccess where="конструктор курса" body={NO_ACCESS_BODY} primary={{ label: "на мой маршрут", href: "/" }} />
        ) : (
          <StateCard code="ERR_NETWORK" where="структура курса" tone="warn" title={NETWORK.title} body={NETWORK.body} primary={{ label: "повторить сейчас", onClick: () => void tree.refetch() }} />
        )}
      </div>
    );
  }
  const t = tree.data;
  if (!stepId) return <PickStep tree={t} />;
  return <StepEditor key={stepId} stepId={stepId} tree={t} />;
}

/** /admin/course без шага: дерево слева, приглашение выбрать шаг. */
function PickStep({ tree }: { tree: Tree }) {
  const first = [...tree.tracks].sort((a, b) => a.position - b.position).find((tr) => tr.steps.length > 0)?.steps[0];
  return (
    <div className="grid min-h-0 grid-cols-[268px_minmax(0,1fr)]" style={{ height: BODY_H }}>
      <StructureTree tree={tree} />
      <div className="overflow-auto px-[22px] py-5">
        <div className="max-w-[640px]">
          <EmptyState
            where="конструктор · шаг"
            title="Выберите шаг слева"
            body={first ? `Откроется редактор: ресурсы, практика, блоки, домашка и гейт. Можно начать с первого шага — «${first.title}».` : "В курсе пока нет шагов. Добавьте первый кнопкой «+ шаг» в любом треке."}
            primary={first ? { label: "открыть первый шаг", href: `/admin/course/${first.id}` } : undefined}
          />
          <div className="mt-5">
            <KbImport />
          </div>
        </div>
      </div>
    </div>
  );
}

type Tab = "res" | "practice" | "blocks" | "files" | "gate";
type ToastState = { tone: ToastTone; title: string; body: ReactNode; action?: { label: string; onClick: () => void } } | null;

function StepEditor({ stepId, tree }: { stepId: string; tree: Tree }) {
  const draft = useDraft(stepId);
  const usage = useUsage(stepId);
  const save = useSaveDraft(stepId);
  const publish = usePublish(stepId);
  // mutateAsync, а не mutate с колбэками: в TanStack v5 колбэки mutate
  // отбрасываются, если их обогнал следующий вызов, и flush() повис бы.
  const editor = useAutosave(draft.data, (body, cb) => save.mutateAsync(body).then(cb.onSuccess, (e: unknown) => cb.onError(e instanceof Error ? e : new Error("не удалось сохранить"))));
  const [tab, setTab] = useState<Tab>("res");
  const [toast, setToast] = useState<ToastState>(null);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (!toast || toast.tone === "err") return;
    const id = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(id);
  }, [toast]);

  if (draft.status === "pending" || (draft.status === "success" && !editor.local)) {
    return (
      <div className="grid min-h-0 grid-cols-[268px_minmax(0,1fr)_336px]" style={{ height: BODY_H }}>
        <StructureTree tree={tree} activeId={stepId} />
        <div className="pt-6">
          <SkeletonRows />
        </div>
        <div />
      </div>
    );
  }
  if (draft.status === "error") {
    const err = draft.error;
    const notFound = err instanceof RequestError && err.status === 404;
    return (
      <div className="grid min-h-0 grid-cols-[268px_minmax(0,1fr)_336px]" style={{ height: BODY_H }}>
        <StructureTree tree={tree} activeId={stepId} />
        <div className="overflow-auto px-[22px] py-5">
          <div className="max-w-[640px]">
            {notFound ? (
              <StateCard code="404" where="шаг" title="Такого шага нет" body="Возможно, его удалили или ссылка устарела. Дерево слева — актуальное: выберите шаг там." primary={{ label: "к списку шагов", href: "/admin/course" }} />
            ) : (
              <StateCard code="ERR_NETWORK" where="черновик шага" tone="warn" title={NETWORK.title} body={NETWORK.body} primary={{ label: "повторить сейчас", onClick: () => void draft.refetch() }} />
            )}
          </div>
        </div>
        <div />
      </div>
    );
  }
  const doc = draft.data;
  const local = editor.local;
  if (!doc || !local) return null;
  const { content } = local;
  const patch = (p: Patch) => editor.update((d) => ({ ...d, content: p(d.content) }));
  const pos = locate(tree, stepId);
  const trackSteps = pos?.track.steps.length ?? 0;
  const trackLower = doc.track.toLowerCase();
  const dirty = doc.dirty || editor.unsaved;
  const published = doc.status === "published";
  const onStep = usage.data?.on_step;
  const opens = pos?.nextInTrack ? `открывает шаг ${pos.index + 2} · ${pos.nextInTrack.title}` : pos?.nextTrack ? `открывает трек «${pos.nextTrack.name}»` : "последний шаг трека";

  const onPublish = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      const ok = await editor.flush();
      if (!ok) {
        setToast({ tone: "err", title: "Черновик не сохранился", body: "Публиковать нечего: последняя правка не дошла до сервера. Проверьте связь и повторите.", action: { label: "повторить", onClick: () => void onPublish() } });
        return;
      }
      await publish.mutateAsync();
      setToast({ tone: "ok", title: "Опубликовано", body: "Студенты видят новую версию шага. Черновик совпадает с опубликованной." });
    } catch (e) {
      setToast({ tone: "err", title: "Не опубликовалось", body: `${e instanceof Error ? e.message : "нет связи"} Черновик сохранён — ничего не потеряно.`, action: { label: "повторить", onClick: () => void onPublish() } });
    } finally {
      setPublishing(false);
    }
  };

  const tabs: { id: Tab; label: string; count: string }[] = [
    { id: "res", label: "ресурсы", count: String(content.resources.length) },
    { id: "practice", label: "практика", count: String(content.practice.base.length + content.practice.apply.length + (content.practice.extra?.length ?? 0)) },
    { id: "blocks", label: "блоки", count: `${blocksOn(content)}/${BLOCKS_TOTAL}` },
    { id: "files", label: "домашка", count: String(content.files.length) },
    { id: "gate", label: "гейт", count: String(content.gate.criteria.length) },
  ];

  return (
    <>
      <div className="flex h-[55px] flex-none items-center gap-4 border-b border-bd px-5" data-testid="topbar">
        <span className="font-mono text-[12px] text-mut" data-testid="crumb">
          курс / {trackLower} / шаг {doc.position}
        </span>
        <span
          className="rounded-sm px-2 py-[3px] font-mono text-[11px]"
          style={published ? { background: "var(--oksoft)", color: "var(--ok)", border: "1px solid var(--ok)" } : { background: "var(--card2)", color: "var(--ink2)", border: "1px solid var(--bd2)" }}
          data-testid="pub-chip"
        >
          {published ? "опубликован" : "черновик"}
        </span>
        <span className="flex-1" />
        <span className="font-mono text-[11.5px] text-mut" data-testid="dirty-label">
          {dirty ? "есть неопубликованные правки" : onStep === undefined ? "изменений нет" : `изменений нет · ${onStep} ${plural(onStep, "студент", "студента", "студентов")} на шаге`}
        </span>
        <SaveIndicator status={editor.status} error={editor.error} retry={() => void editor.retry()} />
        <Link to="/step/$id" params={{ id: stepId }} className="rounded-sm border border-bd2 px-[13px] py-[7px] font-mono text-[12px] text-ink2 no-underline hover:border-acc hover:text-acc">
          предпросмотр
        </Link>
        {dirty ? (
          <Button variant="primary" size="sm" onClick={() => void onPublish()}>
            {publishing ? "публикуем…" : "опубликовать изменения"}
          </Button>
        ) : (
          <Button size="sm" disabled reason="изменений нет">
            опубликовано
          </Button>
        )}
      </div>

      <div className="grid min-h-0 grid-cols-[268px_minmax(0,1fr)_336px]" style={{ height: BODY_H }}>
        <StructureTree tree={tree} activeId={stepId} />

        <div className="flex min-h-0 flex-col gap-4 overflow-auto px-[22px] pb-10 pt-5">
          <div className="flex-none">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <span className="font-mono text-[11px] text-accdim" data-testid="overline">
                {trackLower} · шаг {doc.position} из {trackSteps}
              </span>
              <FeedbackBadge stepId={stepId} />
            </div>
            <input
              aria-label="название шага"
              value={local.title}
              onChange={(e) => editor.update((d) => ({ ...d, title: e.target.value }))}
              className="w-full rounded-[5px] border border-bd2 bg-bg2 px-3 py-2.5 font-mono text-[22px] font-bold tracking-[-.015em] text-ink outline-none focus:border-acc"
            />
            <textarea
              aria-label="вводный текст шага"
              rows={2}
              value={content.intro}
              onChange={(e) => patch((c) => ({ ...c, intro: e.target.value }))}
              className="mt-2.5 block w-full resize-y rounded-[5px] border border-bd2 bg-bg2 px-3 py-2.5 font-sans text-[13.5px] leading-[1.5] text-ink2 outline-none focus:border-acc"
            />
          </div>

          <div className="flex flex-none gap-[3px] border-b border-bd" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className="flex cursor-pointer items-center gap-[7px] border-0 bg-transparent px-[13px] py-[9px] font-mono text-[12.5px]"
                style={{ color: tab === t.id ? "var(--acc)" : "var(--mut)", borderBottom: `2px solid ${tab === t.id ? "var(--acc)" : "transparent"}` }}
              >
                {t.label}
                <span className="text-mut">{t.count}</span>
              </button>
            ))}
          </div>

          <div role="tabpanel">
            {tab === "res" && <ResourcesTab content={content} patch={patch} stepId={stepId} flush={async () => void (await editor.flush())} adopt={editor.adopt} />}
            {tab === "practice" && <PracticeTab content={content} patch={patch} />}
            {tab === "blocks" && <BlocksTab content={content} patch={patch} />}
            {tab === "files" && <FilesTab content={content} patch={patch} />}
            {tab === "gate" && <GateTab content={content} patch={patch} opens={opens} />}
          </div>
        </div>

        <aside className="flex min-h-0 flex-col gap-[13px] overflow-auto border-l border-bd px-4 py-[18px]">
          <ReadinessCard content={content} />
          <DepsCard tree={tree} stepId={stepId} content={content} patch={patch} />
          <UsageCard usage={usage.data} status={usage.status} error={usage.error?.message} retry={() => void usage.refetch()} />
          <HistoryCard history={doc.history} />
        </aside>
      </div>

      {toast && (
        <div className="fixed bottom-5 right-5 z-30 w-[400px] max-w-[calc(100vw-40px)]">
          <Toast tone={toast.tone} title={toast.title} body={toast.body} action={toast.action ? { ...toast.action, onClick: () => { setToast(null); toast.action?.onClick(); } } : { label: "закрыть", onClick: () => setToast(null) }} />
        </div>
      )}
    </>
  );
}

function SaveIndicator({ status, error, retry }: { status: SaveStatus; error: string | null; retry: () => void }) {
  if (status === "error") {
    return (
      <span className="flex items-center gap-2 font-mono text-[11.5px] text-err" role="alert" data-testid="save-state">
        не сохранилось · {error}
        <button type="button" onClick={retry} className="cursor-pointer rounded-sm border border-err bg-transparent px-2 py-1 font-mono text-[11px] text-err">
          повторить
        </button>
      </span>
    );
  }
  const busy = status === "scheduled" || status === "saving";
  return (
    <span className="font-mono text-[11.5px]" style={{ color: busy ? "var(--ink2)" : "var(--mut)" }} data-testid="save-state">
      {busy ? "сохраняем…" : "сохранено"}
    </span>
  );
}

